/**
 * Extensions on `PlanDefinition.action` that say how a clinical-protocol action lands on the care
 * plan — which `CarePlanTopicService` method creates it and which task category the Task carries.
 * `protocol-*` so they read as definition-side, distinct from the `liza-task-category` input on the
 * resulting Task.
 */
export const PROTOCOL_EXTENSIONS = {
  /** `valueCode` ∈ `PROTOCOL_ITEM_KINDS`. */
  ITEM_KIND: 'https://liza.health/fhir/StructureDefinition/protocol-item-kind',
  /** `valueCode` ∈ `TASK_CATEGORY_CODES` (from `@liza/fhir-utils`). */
  TASK_CATEGORY: 'https://liza.health/fhir/StructureDefinition/protocol-task-category',
  /** On the `PlanDefinition` itself: `valueCode` ∈ `PROTOCOL_DELIVERY_MODES`. */
  DELIVERY: 'https://liza.health/fhir/StructureDefinition/protocol-delivery',
} as const;

/**
 * How a protocol action materializes on a patient's care plan. The first four are the care-plan item
 * kinds plus `survey` split out (a survey is a measurement whose Task also names a Questionnaire);
 * `suggestion` lands as a suggested action a clinician accepts, never as an item. Carried as
 * `valueCode` on the `protocol-item-kind` extension.
 */
export const PROTOCOL_ITEM_KINDS = ['measurement', 'survey', 'lifestyle', 'task', 'suggestion'] as const;
export type ProtocolItemKind = (typeof PROTOCOL_ITEM_KINDS)[number];

/**
 * How a protocol's actions reach the care plan. This is a property of the protocol, not of each
 * action: an action always describes a real resource (a measurement with a code and a cadence, a
 * habit, a task), and the delivery mode says whether applying the protocol writes those directly or
 * proposes them for a clinician to accept.
 *
 * - `commit` — actions become live care-plan items, reconciled against what the protocol last
 *   recorded. The default when the extension is absent.
 * - `suggest` — actions become suggested actions a clinician accepts, and nothing reaches the plan
 *   unreviewed.
 *
 * A protocol has exactly one correct mode, so it is declared on the definition rather than passed by
 * the caller: `t2dm-ongoing-measurements` expands onto a freshly bootstrapped chart and must be
 * reviewed, while `t2dm-routine-monitoring` owns its items and reconciles them over time. A variant
 * that wants the other behaviour is a separate definition carrying a different marker.
 *
 * Distinct from the `suggestion` **item kind**, which says what a single action *is* — a proposed
 * prescription needing the eRx catalog — rather than how the protocol delivers.
 */
export const PROTOCOL_DELIVERY_MODES = ['commit', 'suggest'] as const;
export type ProtocolDeliveryMode = (typeof PROTOCOL_DELIVERY_MODES)[number];
